import {
  ClientReachability,
  classifyClientReachability,
} from '../../../helper/clientEmailStatus';
import {
  buildDebtorNoEmailText,
  buildDebtorNoNotificationText,
  buildDebtorNoPortalText,
} from '../../../Invoice/debtorWarnings';
import { IInvoiceFullObject } from '../../../Interfaces/IInvoice';
import { ILegalClient } from '../../../Interfaces/ILegalClient';
import { HealthCheckParams, IHealthCheckInput } from '../types';
import { STATE_IN_PROGRESS } from './legalTransactionState';

/**
 * Regeln zum Bereich „Rechnungen" (#1283).
 *
 * Die Schuldner-Texte kommen aus `Invoice/debtorWarnings`, damit die Wortwahl an
 * einer Stelle liegt. Jede Stufe ist hier eine eigene Regel, damit sie einzeln
 * abschaltbar ist.
 */

/** Bezeichner einer Rechnung für den Fundtext. */
const invoiceLabel = (invoice: IInvoiceFullObject) => {
  if (invoice.InvoiceNumber !== '') {
    return `Rechnung ${invoice.InvoiceNumber}`;
  }

  return invoice.FileName !== ''
    ? `Rechnung „${invoice.FileName}"`
    : 'Rechnung';
};

/**
 * Die Zahlungsschuldner einer Rechnung als Objekte.
 *
 * Gespeichert sind in `LegalClients` nur die IDs; die Erreichbarkeitsangaben
 * stehen am Mandanten des Vorgangs. Das Feld `LegalTransactionLegalClients`
 * taugt dafür nicht: im Vorgang ist es leer, weil
 * `getFullobjectByLegalTransactionId` es nicht setzt, und dort, wo es gesetzt
 * wird, enthält es die Verknüpfungszeilen *aller* Vorgangs-Mandanten ohne
 * Namens- und E-Mail-Felder.
 *
 * Eine ID, die sich nicht auflösen lässt, fällt heraus: das ist der über „Alle
 * Mandanten" gewählte Schuldner, der nicht zum Vorgang gehört (QS #8). Über ihn
 * ist hier keine Aussage möglich — lieber kein Fund als ein falscher.
 */
const debtorsOf = (
  invoice: IInvoiceFullObject,
  legalClients: ILegalClient[]
): ILegalClient[] =>
  invoice.LegalClients.map(id =>
    legalClients.find(client => client.idLegalClient === id)
  ).filter((client): client is ILegalClient => client !== undefined);

/** Rechnung ohne Zahlungsschuldner. */
export const withoutDebtor = ({ legalTransaction }: IHealthCheckInput) =>
  legalTransaction.InvoiceObjectArray.filter(
    invoice => invoice.LegalClients.length === 0
  ).map(
    invoice =>
      `${invoiceLabel(
        invoice
      )}: kein Zahlungsschuldner ausgewählt – bitte einen Schuldner auswählen.`
  );

/**
 * Sammelt die Funde je Rechnung über die Erreichbarkeit ihrer Schuldner.
 * `pick` wählt eine der drei Kategorien, `buildText` liefert den Satz dazu.
 */
const perInvoiceReachability = (
  { legalTransaction }: IHealthCheckInput,
  pick: (reach: ClientReachability) => ILegalClient[],
  buildText: (clients: ILegalClient[]) => string
): string[] =>
  legalTransaction.InvoiceObjectArray.flatMap(invoice => {
    const debtors = debtorsOf(invoice, legalTransaction.LegalClientArray);

    if (debtors.length === 0) {
      return [];
    }

    const affected = pick(classifyClientReachability(debtors));
    return affected.length > 0
      ? [`${invoiceLabel(invoice)}: ${buildText(affected)}`]
      : [];
  });

/** Zahlungsschuldner ohne E-Mail-Adresse. */
export const debtorMissingEmail = (input: IHealthCheckInput) =>
  perInvoiceReachability(input, reach => reach.noEmail, buildDebtorNoEmailText);

/** Zahlungsschuldner ohne Portalzugang. */
export const debtorNoPortalAccess = (input: IHealthCheckInput) =>
  perInvoiceReachability(
    input,
    reach => reach.noPortal,
    buildDebtorNoPortalText
  );

/** Zahlungsschuldner mit deaktiviertem E-Mail-Empfang. */
export const debtorNoNotification = (input: IHealthCheckInput) =>
  perInvoiceReachability(
    input,
    reach => reach.noNotification,
    buildDebtorNoNotificationText
  );

/**
 * Für einige Urkundennummern liegt keine Rechnung vor. Bedingung unverändert
 * aus `InvoiceTab/InvoiceWarning.tsx` übernommen.
 */
export const recordNumberWithoutInvoice = ({
  legalTransaction,
}: IHealthCheckInput) => {
  const recordNumbers = legalTransaction.RecordNumberArray.filter(
    entry => entry.RecordNumberEntry !== ''
  );
  const invoices = legalTransaction.InvoiceObjectArray;

  if (
    recordNumbers.length === 0 ||
    legalTransaction.idLegalTransactionState !== STATE_IN_PROGRESS ||
    invoices.length === 0
  ) {
    return [];
  }

  const hasRecordNumberWithoutInvoice = recordNumbers.some(
    entry =>
      invoices.find(
        invoice =>
          invoice.idLegalTransactionRecordNumber ===
          entry.idLegalTransactionRecordNumber
      ) === undefined
  );

  const hasInvoiceWithoutRecordNumber = invoices.some(
    invoice => invoice.idLegalTransactionRecordNumber === null
  );

  return hasRecordNumberWithoutInvoice || hasInvoiceWithoutRecordNumber
    ? [
        'Für einige Urkundennummern liegt keine Rechnung vor. Bitte prüfen Sie die vorhandenen Dokumente im Reiter „Rechnungen".',
      ]
    : [];
};

/**
 * Nicht erkannte Rechnungen liegen unbearbeitet im Vorgang. `InvoiceArray`
 * sind die hochgeladenen Dateien, denen noch kein Rechnungsobjekt zugeordnet
 * wurde — sie erreichen den Mandanten nicht.
 */
export const unrecognizedInvoices = ({
  legalTransaction,
}: IHealthCheckInput) =>
  legalTransaction.InvoiceArray.length > 0
    ? [
        legalTransaction.InvoiceArray.length === 1
          ? 'Eine Datei liegt als nicht erkannte Rechnung im Vorgang und wurde noch keiner Rechnung zugeordnet.'
          : `${legalTransaction.InvoiceArray.length} Dateien liegen als nicht erkannte Rechnungen im Vorgang und wurden noch keiner Rechnung zugeordnet.`,
      ]
    : [];

/** Rechnung ohne Betrag oder mit Betrag 0. */
export const withoutAmount = ({ legalTransaction }: IHealthCheckInput) =>
  legalTransaction.InvoiceObjectArray.filter(
    invoice => invoice.Total === null || invoice.Total === 0
  ).map(
    invoice =>
      `${invoiceLabel(invoice)}: es ist kein Betrag hinterlegt (0,00 €).`
  );

/**
 * Zahlungsziel überschritten und noch nicht bezahlt. `InvoicePaids` sind die
 * verbuchten Zahlungen; ist keine vorhanden, gilt die Rechnung als offen.
 *
 * Parameter `graceDays` verschiebt die Fälligkeit, damit eine Kanzlei nicht am
 * Tag nach dem Zahlungsziel schon gewarnt wird.
 */
export const paymentOverdue = (
  { legalTransaction }: IHealthCheckInput,
  params: HealthCheckParams
) => {
  const graceDays = Number(params.graceDays ?? 0);
  const threshold = new Date();
  threshold.setHours(0, 0, 0, 0);
  threshold.setDate(threshold.getDate() - graceDays);

  return legalTransaction.InvoiceObjectArray.filter(invoice => {
    if (invoice.InvoicePaids.length > 0) {
      return false;
    }

    if (!invoice.PaymentDueDate) {
      return false;
    }

    const dueDate = new Date(invoice.PaymentDueDate);
    return !Number.isNaN(dueDate.getTime()) && dueDate < threshold;
  }).map(
    invoice =>
      `${invoiceLabel(invoice)}: das Zahlungsziel vom ${new Date(
        invoice.PaymentDueDate
      ).toLocaleDateString(
        'de-DE'
      )} ist überschritten und es ist keine Zahlung verbucht.`
  );
};
